import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { getApiPermissionsMapFactory } from '../../configs/ApiPermissionsMap';

import { getApiRoute, getApiRoutesMapFactory } from '../../configs/ApiRoutesMap';
import { IdelinkModuleTheme, useIdelinkTheme } from '../../configs/IdeLinkTheme';
import { NavbarModuleItem } from '../../../libs/IdeLink.Web.Common/src/components/NavBar/components/NavBarItem/NavBarItem';
import { useIsPermissionsInitialized } from '../../../libs/IdeLink.Web.Common/src/Hooks/useIsPermissionsInitialized';
import { RouteItem } from '../../../libs/IdeLink.Web.Common/src/types/RouteItem';
import { PrivateRoute } from '../../routes/PrivateRoute';
import { Module } from '../Module';
import { OffersModuleContext, useOffersConfig } from './Configs/OffersConfig';
import { PackagedOrderCreatePageLoading } from './Pages/PackagedOrders/PackagedOrderCreatePage/PackagedOrderCreatePageLoading';
import { PackagedOrderDetailsPageLoading } from './Pages/PackagedOrders/PackagedOrderDetailsPage/PackagedOrderDetailsPageLoading';
import { PackagedOrderListPageLoading } from './Pages/PackagedOrders/PackagedOrderListPage/PackagedOrderListPageLoading';
import { TunableOfferCreatePageLoading } from './Pages/TunnableOffers/TunableOfferCreatePage/TunableOfferCreatePageLoading';
import { TunableOfferDetailsPageLoading } from './Pages/TunnableOffers/TunableOfferDetailsPage/TunableOfferDetailsPageLoading';
import { OffersPermissions } from './Services/OffersPermissions';

//TODO : maybe rename to OfferModule : (PackagedOrders - TunableOffers - GoodDeals)
export class OffersModule {
    //! MIDAS: offers, FLUIZY, DEMOLINK, IRRITALK : gooddeals
    public static OFFER_LIST_API_ROUTE = 'offers';
    public static OFFER_LIST_API_ROUTE_KEY = 'OFFER_LIST_API_ROUTE_KEY';

    //! MIDAS: packagedorders, FLUIZY, DEMOLINK, IRRITALK : gooddeals
    //todo remarque : je trouve ça inutile, ça devrait s'appeler offers
    public static PACKAGED_ORDERS_API_ROUTE = 'packagedorders';
    public static PACKAGED_ORDERS_API_ROUTE_KEY = 'PACKAGED_ORDERS_API_ROUTE_KEY';

    public static PACKAGED_ORDER_LIST = () =>
        `/${getApiRoute(OffersModule.PACKAGED_ORDERS_API_ROUTE_KEY)}`;
    public static PACKAGED_ORDER_CREATE = () => `${OffersModule.PACKAGED_ORDER_LIST()}/create`;
    public static PACKAGED_ORDER_DETAILS = () => `${OffersModule.PACKAGED_ORDER_LIST()}/:id`;

    public static TUNABLE_OFFER_BASE: string = '/tunable-offers';
    public static TUNABLE_OFFER_CREATE: string = `${OffersModule.TUNABLE_OFFER_BASE}/create`;
    public static TUNABLE_OFFER_DETAILS: string = `${OffersModule.TUNABLE_OFFER_BASE}/:id`;

    //#region //* PACKAGED ORDERS

    private static getPackagedOrderListRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: OffersModule.PACKAGED_ORDER_LIST(),
            component: OffersListPage,
        },
    });
    private static getPackagedOrderCreateRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: OffersModule.PACKAGED_ORDER_CREATE(),
            component: OffersCreatePage,
        },
    });
    private static getPackagedOrderDetailsRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: OffersModule.PACKAGED_ORDER_DETAILS(),
            component: OffersDetailsPage,
        },
    });
    public static packagedOrderDetailsRoute = (packagedOrderId: string): string => {
        return `${OffersModule.PACKAGED_ORDER_DETAILS()}`.replace(':id', packagedOrderId);
    };

    public static preloadPackagedOrderListPage = () => {
        PackagedOrderListPageLoading.preload();
        PackagedOrderDetailsPageLoading.preload();
        PackagedOrderCreatePageLoading.preload();
    };

    //#endregion

    //#region //* TUNABLE OFFERS

    private static getTunableOfferCreateRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: OffersModule.TUNABLE_OFFER_CREATE,
            component: TunableOfferCreatePageLoading,
        },
    });
    private static getTunableOfferDetailsRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: OffersModule.TUNABLE_OFFER_DETAILS,
            component: TunableOfferDetailsPageLoading,
        },
    });

    public static tunableOfferDetailsRoute = (tunableOfferId: string): string => {
        return `${OffersModule.TUNABLE_OFFER_DETAILS}`.replace(':id', tunableOfferId);
    };

    //#endregion

    public static getOffersRoutes = (): Array<RouteItem> => {
        //const { showCreateTunable } = useOffersConfig();
        let offersRoutes = [
            OffersModule.getPackagedOrderListRoute(),
            OffersModule.getPackagedOrderCreateRoute(),
            OffersModule.getPackagedOrderDetailsRoute(),
        ];
        //if (showCreateTunable) {
        offersRoutes.push(
            OffersModule.getTunableOfferCreateRoute(),
            OffersModule.getTunableOfferDetailsRoute()
        );
        //}
        return offersRoutes;
    };

    public static getOffersModuleNavbar = (offersTheme: IdelinkModuleTheme): NavbarModuleItem => {
        return {
            icon: offersTheme.navbarIcon,
            label: <FormattedMessage id="navbar.packagedOrders" />,
            route: OffersModule.PACKAGED_ORDER_LIST(),
            onMouseOver: () => OffersModule.preloadPackagedOrderListPage(),
            childRoutes: [OffersModule.TUNABLE_OFFER_BASE],
        };
    };
}

const OffersListPage = () => {
    return (
        <OfferModuleConfig>
            <PackagedOrderListPageLoading />
        </OfferModuleConfig>
    );
};

const OffersCreatePage = () => {
    return (
        <OfferModuleConfig>
            <PackagedOrderCreatePageLoading />
        </OfferModuleConfig>
    );
};

const OffersDetailsPage = () => {
    return (
        <OfferModuleConfig>
            <PackagedOrderDetailsPageLoading />
        </OfferModuleConfig>
    );
};

const OfferModuleConfig = ({ children }: React.PropsWithChildren<{}>) => {
    const value = useOffersConfig();
    const theme = useIdelinkTheme();

    return (
        <OffersModuleContext.Provider
            value={{
                ...value,
                i18nKeyPrefix: 'packagedOrders',
                listUrl: OffersModule.PACKAGED_ORDER_LIST(),
                createUrl: OffersModule.PACKAGED_ORDER_CREATE(),
                editUrl: OffersModule.packagedOrderDetailsRoute,
                apiResourceName: getApiRoute(OffersModule.PACKAGED_ORDERS_API_ROUTE_KEY), //Keep retro compatibility
                listCustomResourceName: getApiRoute(OffersModule.OFFER_LIST_API_ROUTE_KEY), //Keep retro compatibility
                theme: theme.offers,
            }}
        >
            {children}
        </OffersModuleContext.Provider>
    );
};

export type IdkOffersModuleProps = {
    apiResourceName?: string; // optional for legacy, should be set on new project.
    permissionKey?: string;
};
export const IdkOffersModule = ({ apiResourceName, permissionKey }: IdkOffersModuleProps) => {
    const ideLinkTheme = useIdelinkTheme();

    useMemo(() => {
        if (apiResourceName) {
            //PGU 5/10/2022: J'avoue ne pas comprendre la différence entre packageOrder et offerList.
            // Il n'y a que sur MIDAS que les valeurs sont différentes.
            // Refactor à prévoir pour éviter de passer par une Factory ici mais directement passer les valeurs à OfferModuleConfig via OffersModule.getOffersRoutes(apiResourceName)
            getApiRoutesMapFactory.register('**', () => ({
                [OffersModule.PACKAGED_ORDERS_API_ROUTE_KEY]: apiResourceName,
                [OffersModule.OFFER_LIST_API_ROUTE_KEY]: apiResourceName,
            }));
        }
    }, [apiResourceName]);

    useMemo(() => {
        if (permissionKey) {
            const defaultPermissionMap = getApiPermissionsMapFactory();
            const getPermissionsMap = () => ({
                ...defaultPermissionMap,
                [OffersPermissions.OFFERS_KEY]: permissionKey,
            });
            getApiPermissionsMapFactory.register('**', getPermissionsMap);
        }
    }, [permissionKey]);

    //this module need the permissions to load
    if (!useIsPermissionsInitialized()) {
        return null;
    }

    return (
        <Module
            moduleKey="OffersModule"
            routes={OffersModule.getOffersRoutes()}
            navBarItems={[OffersModule.getOffersModuleNavbar(ideLinkTheme.offers)]}
            permissionsItems={[OffersPermissions.getOffersTab()]}
        />
    );
};
